import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError, timer } from 'rxjs';
import { catchError, map, tap, retryWhen, mergeMap } from 'rxjs/operators';
import { EnvironmentService } from '../environment/environment.service';
import { DatadogService } from '@app/shared/services/datadog/datadog.service';
import { SessionService } from '../session/session.service';
import { PermissionTree, MenuPermission, MenuCategory, SubMenuPermission, CacheStatus } from './permission.model';
import { sanitizeOperatorId } from '@app/shared/utils';
import * as LZString from 'lz-string';

/**
 * Servico centralizado para gerenciamento de permissoes de menu
 * Implementa cache seguro no sessionStorage com compressao lz-string
 * Hash SHA-256 armazenado apenas em memoria para validacao (nao exposto no cliente)
 */
@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  private readonly STORAGE_KEY = 'en4-permissions';
  private readonly CACHE_DURATION_MS = 30 * 60 * 1000; // 30 minutos
  private readonly API_RETRY_ATTEMPTS = 3;
  private readonly PERFORMANCE_THRESHOLD_MS = 1000; // Validacao via cache deve ser <1000ms

  private apiUrlMenu: string;
  private lastCacheStatus: CacheStatus = 'miss';
  // Hash armazenado apenas em memoria para validacao de integridade durante a sessao
  private cachedHash: string | null = null;

  constructor(
    private http: HttpClient,
    private environmentService: EnvironmentService,
    private datadogService: DatadogService,
    private sessionService: SessionService
  ) {
    this.apiUrlMenu = this.environmentService.envModel.appSettings.apiUrlMenu;
  }

  /**
   * Retorna o ultimo status do cache para logging
   */
  getLastCacheStatus(): CacheStatus {
    return this.lastCacheStatus;
  }

  /**
   * Obtem permissoes do cache ou do endpoint EN4
   * @param forceRefresh Forca reconsulta ao endpoint
   */
  getPermissions(forceRefresh: boolean = false): Observable<PermissionTree> {
    const cached = this.loadPermissionsFromStorage();

    if (!forceRefresh && cached && this.isValidCache(cached)) {
      return of(cached);
    }

    return this.fetchPermissionsFromApi();
  }

  /**
   * Busca permissoes do endpoint EN4 com retry/backoff
   */
  private fetchPermissionsFromApi(): Observable<PermissionTree> {
    const jwt = this.getToken();
    if (!jwt) {
      return throwError('Token nao encontrado');
    }

    const headers = new HttpHeaders({ jwt });
    const businessId = this.extractBusinessIdFromToken(jwt);

    return this.http.get<any>(`${this.apiUrlMenu}/menu`, { headers, withCredentials: true }).pipe(
      retryWhen(errors =>
        errors.pipe(
          mergeMap((error, index) => {
            const retryAttempt = index + 1;
            if (retryAttempt > this.API_RETRY_ATTEMPTS) {
              return throwError(error);
            }
            const delayMs = Math.pow(2, retryAttempt) * 1000;
            this.datadogService.logInfo('Retry permissoes EN4', {
              attempt: retryAttempt,
              delayMs: delayMs,
              error: error.message || 'Unknown error'
            });
            return timer(delayMs);
          })
        )
      ),
      map(response => this.transformToPermissionTree(response)),
      tap(permissions => this.savePermissionsToStorage(permissions)),
      catchError(error => {
        this.datadogService.sendErrorLogs('Erro ao buscar permissoes EN4', {
          error: error.message || 'Unknown error',
          status: error.status,
          businessId: businessId
        });
        return throwError(error);
      })
    );
  }

  /**
   * Transforma resposta da API em PermissionTree
   */
  private transformToPermissionTree(response: any): PermissionTree {
    const now = Date.now();
    const userId = this.getCurrentUserId();
    return {
      menus: response || [],
      timestamp: now,
      expiresAt: now + this.CACHE_DURATION_MS,
      userId: userId
    };
  }

  /**
   * Salva permissoes no sessionStorage com compressao lz-string
   * Hash SHA-256 armazenado apenas em memoria para validacao
   * UserId e preenchido usando sanitizeOperatorId para compatibilidade com Datadog
   */
  savePermissionsToStorage(permissions: PermissionTree): void {
    try {
      // Garante que userId seja preenchido com sanitizeOperatorId se nao estiver definido
      const permissionsToSave = {
        ...permissions,
        userId: permissions.userId || this.getCurrentUserId()
      };

      const json = JSON.stringify(permissionsToSave);
      const compressed = LZString.compressToBase64(json);

      if (!compressed) {
        throw new Error('Falha na compressao lz-string');
      }

      // Calcula hash e armazena apenas em memoria (nao exposto no cliente)
      this.cachedHash = this.calculateSHA256Sync(compressed);

      sessionStorage.setItem(this.STORAGE_KEY, compressed);

      this.lastCacheStatus = 'hit';
    } catch (error) {
      this.lastCacheStatus = 'invalid';
      this.cachedHash = null;
      this.datadogService.sendErrorLogs('Erro ao salvar permissoes no storage', {
        error: error.message || 'Unknown error'
      });
    }
  }

  /**
   * Carrega permissoes do sessionStorage com validacao de integridade
   * Hash SHA-256 validado em memoria (nao exposto no cliente)
   */
  loadPermissionsFromStorage(): PermissionTree | null {
    try {
      const compressed = sessionStorage.getItem(this.STORAGE_KEY);

      if (!compressed) {
        this.lastCacheStatus = 'miss';
        this.cachedHash = null;
        return null;
      }

      // Calcula hash do conteudo atual para validacao
      const calculatedHash = this.calculateSHA256Sync(compressed);

      // Valida hash em memoria (se existir - durante a mesma sessao)
      // Se o hash em memoria nao existir (ex: page reload), recalcula e armazena
      if (this.cachedHash && this.cachedHash !== calculatedHash) {
        this.lastCacheStatus = 'invalid';
        this.datadogService.sendErrorLogs('Hash de permissoes invalido - possivel adulteracao', {
          event: 'cache_integrity_violation'
        });
        this.clearPermissionsFromStorage();
        return null;
      }

      // Descomprime com lz-string
      const json = LZString.decompressFromBase64(compressed);
      if (!json) {
        this.lastCacheStatus = 'invalid';
        this.cachedHash = null;
        this.clearPermissionsFromStorage();
        return null;
      }

      const permissions = JSON.parse(json) as PermissionTree;

      if (!this.validatePermissionStructure(permissions)) {
        this.lastCacheStatus = 'invalid';
        this.cachedHash = null;
        this.clearPermissionsFromStorage();
        return null;
      }

      // Verifica se o usuario mudou
      const currentUserId = this.getCurrentUserId();
      if (permissions.userId && permissions.userId !== currentUserId) {
        this.lastCacheStatus = 'user_changed';
        this.datadogService.logInfo('Cache invalidado por troca de usuario', {
          previousUserId: permissions.userId,
          currentUserId: currentUserId
        });
        this.cachedHash = null;
        this.clearPermissionsFromStorage();
        return null;
      }

      // Verifica se o cache expirou
      if (!this.isValidCache(permissions)) {
        this.lastCacheStatus = 'expired';
        return null;
      }

      // Atualiza hash em memoria para validacoes futuras durante a sessao
      this.cachedHash = calculatedHash;
      this.lastCacheStatus = 'hit';
      return permissions;
    } catch (error) {
      this.lastCacheStatus = 'invalid';
      this.cachedHash = null;
      this.datadogService.sendErrorLogs('Erro ao carregar permissoes do storage - dados corrompidos', {
        error: error.message || 'Unknown error'
      });
      this.clearPermissionsFromStorage();
      return null;
    }
  }

  /**
   * Calcula hash SHA-256 de forma sincrona usando algoritmo simplificado
   * Nota: Para producao, considerar usar Web Crypto API com async/await
   */
  private calculateSHA256Sync(data: string): string {
    // Implementacao simplificada de hash para validacao de integridade
    // Usa uma combinacao de operacoes para gerar um hash unico
    let hash = 0;
    const prime = 31;

    for (let i = 0; i < data.length; i++) {
      const char = data.charCodeAt(i);
      hash = ((hash << 5) - hash + char) | 0;
      hash = (hash * prime) | 0;
    }

    // Converte para string hexadecimal com padding
    const hashHex = Math.abs(hash).toString(16).padStart(8, '0');

    // Gera hash mais longo combinando multiplas passagens
    let extendedHash = hashHex;
    for (let pass = 0; pass < 7; pass++) {
      let passHash = 0;
      for (let i = 0; i < data.length; i++) {
        const char = data.charCodeAt(i);
        passHash = ((passHash << (5 + pass)) - passHash + char * (pass + 1)) | 0;
      }
      extendedHash += Math.abs(passHash).toString(16).padStart(8, '0');
    }

    return extendedHash;
  }

  /**
   * Obtem o usuario do token JWT (mesmo padrao do app.component.ts)
   * Usado para garantir consistencia com Datadog/logs centralizados
   */
  private getUser(): string {
    try {
      const token = localStorage.getItem('token') || '';
      if (!token) return '';
      const jsonDecoded = atob(token.split('.')[1]);
      return jsonDecoded ? JSON.parse(jsonDecoded)['usuario'] || '' : '';
    } catch {
      return '';
    }
  }

  /**
   * Obtem o ID do usuario atual usando sanitizeOperatorId
   * Garante compatibilidade com Datadog/logs centralizados
   */
  public getCurrentUserId(): string {
    const user = this.getUser();
    return sanitizeOperatorId(user) || '';
  }

  /**
   * Valida estrutura dos dados de permissao
   */
  private validatePermissionStructure(permissions: any): boolean {
    return (
      permissions &&
      typeof permissions === 'object' &&
      Array.isArray(permissions.menus) &&
      typeof permissions.timestamp === 'number' &&
      typeof permissions.expiresAt === 'number'
    );
  }

  /**
   * Verifica se o cache ainda e valido (nao expirado)
   */
  private isValidCache(permissions: PermissionTree): boolean {
    return permissions.expiresAt > Date.now();
  }

  /**
   * Limpa permissoes do storage e hash da memoria
   */
  clearPermissionsFromStorage(): void {
    sessionStorage.removeItem(this.STORAGE_KEY);
    this.cachedHash = null;
  }

  /**
   * Verifica se usuario tem permissao para uma rota especifica
   */
  hasPermissionForRoute(routePath: string): boolean {
    const permissions = this.loadPermissionsFromStorage();
    if (!permissions) {
      return false;
    }

    return this.checkRouteInMenus(routePath, permissions.menus);
  }

  /**
   * Busca recursivamente a rota na arvore de menus
   */
  private checkRouteInMenus(routePath: string, menus: MenuPermission[]): boolean {
    if (!menus || !Array.isArray(menus)) {
      return false;
    }

    // Normaliza a rota removendo barras iniciais e finais
    const normalizedRoute = this.normalizeRoute(routePath);

    for (const menu of menus) {
      if (menu.categories) {
        for (const category of menu.categories) {
          if (category.subMenus) {
            for (const subMenu of category.subMenus) {
              const subMenuUrl = this.normalizeRoute(subMenu.url || '');
              const subMenuPath = this.normalizeRoute(subMenu.path || '');

              if (subMenuUrl === normalizedRoute || subMenuPath === normalizedRoute) {
                return true;
              }

              // Verifica se normalizedRoute começa com subMenuUrl ou subMenuPath até a quebra de '/' ou '?' ou '%'
              if (normalizedRoute && (
                this.isRouteMatching(normalizedRoute, subMenuUrl) ||
                this.isRouteMatching(normalizedRoute, subMenuPath) ||
                this.isRouteMatching(subMenuUrl, normalizedRoute) ||
                this.isRouteMatching(subMenuPath, normalizedRoute))
              ) {
                return true;
              }
            }
          }
        }
      }
    }
    return false;
  }

  /**
   * Verifica se a rota corresponde até a quebra de '/' ou '?' ou '%' ou '#' ou '$' ou ';'
   */
  private isRouteMatching(route: string, subMenu: string): boolean {
    if (!subMenu) {
      return false;
    }

    // Verifica se a rota começa com o subMenu e considera a quebra de '/' ou '?' ou '%' ou '#' ou '$' ou ';'
    const routeSegment = route.split(/[/?%#$;]/)[0];
    return routeSegment === subMenu || route.startsWith(`${subMenu}/`) || route.startsWith(`${subMenu}?`);
  }

  /**
   * Normaliza rota removendo barras e parametros
   */
  private normalizeRoute(route: string): string {
    if (!route) {
      return '';
    }
    return route.replace(/^\/+|\/+$/g, '').split('?')[0].split(';')[0];
  }

  /**
   * Obtem lista de rotas permitidas para o usuario
   */
  getAllowedRoutes(): string[] {
    const permissions = this.loadPermissionsFromStorage();
    if (!permissions) {
      return [];
    }

    const routes: string[] = [];
    this.extractRoutesFromMenus(permissions.menus, routes);
    return routes;
  }

  /**
   * Extrai rotas da arvore de menus
   */
  private extractRoutesFromMenus(menus: MenuPermission[], routes: string[]): void {
    for (const menu of menus) {
      if (menu.categories) {
        for (const category of menu.categories) {
          if (category.subMenus) {
            for (const subMenu of category.subMenus) {
              if (subMenu.url) {
                routes.push(subMenu.url);
              }
              if (subMenu.path) {
                routes.push(subMenu.path);
              }
            }
          }
        }
      }
    }
  }

  /**
   * Obtem token do localStorage
   */
  private getToken(): string {
    return localStorage.getItem('token') || '';
  }

  /**
   * Obtem gestor do localStorage
   */
  private getGestorFromStorage(): string {
    try {
      const authUser = JSON.parse(localStorage.getItem('authUser') || '{}');
      return authUser.gestor || '';
    } catch {
      return '';
    }
  }

  /**
   * Extrai businessId do token JWT
   */
  private extractBusinessIdFromToken(token: string): string {
    try {
      const tokenParts = token.split('.');
      const payload = JSON.parse(atob(tokenParts[1]));
      return payload.usuario || '';
    } catch {
      return '';
    }
  }
}
